<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { useLazyQuery } from '@vue/apollo-composable';
import { useRecaptchaToken } from '@/composables/recaptcha';
import { useRouter } from 'vue-router';
import { LOGIN_URL } from '@/graphql/queries';
import { useField, useForm } from 'vee-validate';
import {
    VBtn,
    VForm,
    VContainer,
    VRow,
    VCol,
    VTextField,
} from 'vuetify/components';
import { toTypedSchema } from '@vee-validate/zod';
import * as zod from 'zod';
import { RouteName } from '@/router';
import { ErrorType } from '@/gql/graphql';
import { useErrorHandler } from '@/composables/errors';
import { RouterLink } from 'vue-router';
import { useMixpanel } from '@/composables/mixpanel';

const { t } = useI18n({
    inheritLocale: true,
    messages: {
        nl: {
            log_in_with_password: 'Inloggen met wachtwoord',
            log_in_with_secure_link: 'Inloggen met secure link',
            email_label: 'E-mail',
            invalid_recaptcha: 'Ongeldige ReCaptcha',
            forgot_password: 'Wachtwoord vergeten',
            missing_email: 'E-mail veld is verplicht',
        },
        en: {
            log_in_with_password: 'Log in with password',
            log_in_with_secure_link: 'Log in using secure link',
            email_label: 'Email',
            invalid_recaptcha: 'Invalid ReCaptcha',
            forgot_password: 'Forgot password',
            missing_email: 'Email is required',
        },
    },
});

const { root: recaptchaRoot, getToken } = useRecaptchaToken();

const { handleError } = useErrorHandler();
const authStore = useAuthStore();
const router = useRouter();
const mixpanel = useMixpanel();

function handleClearEmail() {
    // Vuetify defaults to setting the value to null, which triggers a different error than "required field"
    // Also causes the password reset to autofill 'undefined' instead of empty string
    email.value.value = '';
}

// Form validation
const validationSchema = toTypedSchema(
    zod.object({
        email: zod
            .string()
            .min(1, t('error.required_field'))
            .email(t('error.invalid_email')),
    })
);

const { handleSubmit } = useForm({
    validationSchema,
});

const email = useField<string | undefined>('email');
const recaptchaToken = ref('');

const variables = computed(() => ({
    email: email.value.value,
    recaptchaToken: recaptchaToken.value,
}));

const {
    load: loadLoginUrl,
    onResult: onLoginUrlResult,
    loading: loginUrlLoading,
    onError: onLoginUrlError,
} = useLazyQuery(LOGIN_URL, variables, {
    fetchPolicy: 'no-cache',
});

const onSubmit = handleSubmit(async () => {
    try {
        recaptchaToken.value = await getToken();

        await loadLoginUrl();
    } catch (err) {
        console.error(err);
    }
});

onLoginUrlResult((response) => {
    if (response.data.loginUrl.url) {
        window.location.href = response.data.loginUrl.url;
        return;
    }

    if (!email.value.value) {
        handleError(new Error(t('missing_email')));
        return;
    }

    authStore.setCredentials({
        email: email.value.value,
        password: '',
    });

    router.push({
        name: RouteName['account.password'],
    });
});

onLoginUrlError(({ graphQLErrors }) => {
    graphQLErrors.forEach((err) => {
        if (err.extensions?.type === ErrorType.INVALID_RECAPTCHA) {
            handleError(new Error(t('invalid_recaptcha')));
            return;
        }
    });
});

async function loginWithSecureLink() {
    if (email.value.value) {
        authStore.setCredentials({
            email: email.value.value,
            password: '',
        });
    }

    mixpanel?.track('Login with secure link');

    await router.push({
        name: RouteName['account.secure-link-request'],
        query: {
            redirect: 'login',
        },
    });
}
</script>

<template>
    <v-form @submit.prevent="onSubmit">
        <v-container class="pa-0">
            <v-row no-gutters align-content="stretch">
                <v-col cols="12">
                    <v-text-field
                        v-model="email.value.value"
                        :label="t('email_label')"
                        :placeholder="t('email_label')"
                        :error-messages="email.errorMessage.value"
                        autofocus
                        type="email"
                        name="email"
                        hide-details="auto"
                        class="mb-4"
                        @click:clear="handleClearEmail"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" align="right" class="mb-8">
                    <router-link
                        variant="text"
                        :to="{ name: RouteName['account.forgot-password'] }"
                        class="text-primary-10 text-body-small"
                    >
                        {{ t('forgot_password') }}
                    </router-link>
                </v-col>
                <div ref="recaptchaRoot" />
                <v-col>
                    <v-btn
                        :loading="loginUrlLoading"
                        type="submit"
                        color="primary"
                        :disabled="!email.meta.dirty"
                        block
                        class="mb-3"
                        >{{ t('log_in_with_password') }}</v-btn
                    >
                </v-col>
                <v-col>
                    <v-btn
                        prepend-icon="key"
                        type="button"
                        color="primary"
                        class="elevation-0"
                        block
                        :disabled="!email.meta.dirty"
                        variant="outlined"
                        @click="loginWithSecureLink"
                        >{{ t('log_in_with_secure_link') }}</v-btn
                    >
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>
